import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { api, ApiError, type MetaPermissionStatus } from '../lib/api.js';
import { useFormatters, useI18n } from '../i18n/LanguageProvider.js';
import type { TranslationKey } from '../i18n/translations.js';
import { Icon } from './Icons.js';
import { MailSettings } from './MailSettings.js';
import { SignupQueue } from './SignupQueue.js';
import { TenantUsers } from './TenantUsers.js';

/**
 * The platform console — SUPERADMIN only.
 *
 * Separate from the tenant's own Account page on purpose: this is where the
 * operator of the installation manages *other people's* accounts, and mixing
 * the two would make it easy to change the wrong tenant's plan.
 */
export function AdminPanel() {
  const [tab, setTab] = useState<
    'dashboard' | 'tenants' | 'plans' | 'permissions' | 'signups' | 'mail'
  >('dashboard');
  const { t } = useI18n();

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{t('admin.title')}</h2>
        <div className="segmented">
          <button
            type="button"
            className={tab === 'dashboard' ? 'active' : ''}
            onClick={() => setTab('dashboard')}
          >
            {t('admin.tab.dashboard')}
          </button>
          <button
            type="button"
            className={tab === 'tenants' ? 'active' : ''}
            onClick={() => setTab('tenants')}
          >
            {t('admin.tab.tenants')}
          </button>
          <button
            type="button"
            className={tab === 'plans' ? 'active' : ''}
            onClick={() => setTab('plans')}
          >
            {t('admin.tab.plans')}
          </button>
          <button
            type="button"
            className={tab === 'permissions' ? 'active' : ''}
            onClick={() => setTab('permissions')}
          >
            {t('admin.tab.permissions')}
          </button>
          <button
            type="button"
            className={tab === 'signups' ? 'active' : ''}
            onClick={() => setTab('signups')}
          >
            {t('admin.signups')}
          </button>
          <button
            type="button"
            className={tab === 'mail' ? 'active' : ''}
            onClick={() => setTab('mail')}
          >
            {t('mail.tab')}
          </button>
        </div>
      </div>

      {tab === 'dashboard' && <Dashboard />}
      {tab === 'tenants' && <Tenants />}
      {tab === 'plans' && <Plans />}
      {tab === 'permissions' && <MetaPermissions />}
      {tab === 'signups' && <SignupQueue />}
      {tab === 'mail' && <MailSettings />}
    </section>
  );
}

function Dashboard() {
  const { t } = useI18n();
  const fmt = useFormatters();
  const stats = useQuery({
    queryKey: ['admin-stats'],
    queryFn: api.adminStats,
  });

  if (stats.isLoading) return <div className="loading">{t('common.loading')}</div>;
  if (stats.isError) return <div className="notice error">{t('admin.superAdminOnly')}</div>;

  const s = stats.data!.stats;

  return (
    <>
      <div className="stat-grid">
        <Stat label={t('admin.stat.tenants')} value={fmt.number(s.tenants.total)} accent>
          <span className="badge ok">
            {s.tenants.active} {t('admin.active')}
          </span>
          {s.tenants.suspended > 0 && (
            <span className="badge warn">
              {s.tenants.suspended} {t('admin.suspended')}
            </span>
          )}
        </Stat>
        <Stat label={t('admin.stat.users')} value={fmt.number(s.users)} />
        <Stat label={t('admin.stat.connections')} value={fmt.number(s.connections)} />
        <Stat label={t('admin.stat.pages')} value={fmt.number(s.pages)} />
        <Stat label={t('admin.stat.posts')} value={fmt.number(s.postsPublished)} accent />
        <Stat label={t('admin.stat.agentCalls')} value={fmt.number(s.agentCallsThisMonth)} />
        <Stat label={t('admin.stat.replies')} value={fmt.number(s.repliesThisMonth)} />
        <Stat label={t('admin.stat.jobs')} value={fmt.number(s.jobs.total)}>
          <span className="badge ok">{s.jobs.succeeded}</span>
          {s.jobs.partial > 0 && <span className="badge warn">{s.jobs.partial}</span>}
          {s.jobs.failed > 0 && <span className="badge err">{s.jobs.failed}</span>}
        </Stat>
      </div>

      <h3 style={{ marginTop: 26 }}>{t('admin.planDistribution')}</h3>
      <div className="grid-wrap">
        <table className="grid">
          <thead>
            <tr>
              <th>{t('admin.plan')}</th>
              <th>{t('admin.tenantsOnPlan')}</th>
            </tr>
          </thead>
          <tbody>
            {s.planDistribution.map((row) => (
              <tr key={row.code}>
                <td>
                  <strong>{row.name}</strong> <span className="mono tiny muted">{row.code}</span>
                </td>
                <td>{fmt.number(row.tenants)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Stat({
  label,
  value,
  accent,
  children,
}: {
  label: string;
  value: string;
  accent?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div className={`stat-card${accent ? ' accent' : ''}`}>
      <span className="stat-label">{label}</span>
      <span className="stat-value">{value}</span>
      {children && <div className="stat-extra">{children}</div>}
    </div>
  );
}

function Tenants() {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const fmt = useFormatters();
  const [notice, setNotice] = useState<{
    kind: 'ok' | 'error';
    text: string;
  } | null>(null);
  const [form, setForm] = useState({ name: '', slug: '', planCode: '' });
  /** Which account's users are open, if any. */
  const [openTenant, setOpenTenant] = useState<{
    id: string;
    name: string;
  } | null>(null);

  /**
   * Renaming, and deleting.
   *
   * Both were missing entirely: a name typed wrong at creation was permanent,
   * and an account created by mistake could never be removed. The rename is
   * inline; the delete asks for the name to be typed back, because an account is
   * identified by its name in this list and typing it is the one confirmation a
   * misplaced click cannot give.
   */
  const rename = useMutation({
    mutationFn: (params: { id: string; name: string }) =>
      api.renameTenant(params.id, { name: params.name }),
    onSuccess: () => {
      setNotice({ kind: 'ok', text: t('admin.tenantRenamed') });
      void queryClient.invalidateQueries({ queryKey: ['admin-tenants'] });
    },
    onError: (error) => setNotice({ kind: 'error', text: (error as Error).message }),
  });

  const destroy = useMutation({
    mutationFn: (params: { id: string; confirmName: string; force?: boolean }) =>
      api.deleteTenant(params.id, params.confirmName, params.force ?? false),
    onSuccess: () => {
      setOpenTenant(null);
      setNotice({ kind: 'ok', text: t('admin.tenantDeleted') });
      void queryClient.invalidateQueries({ queryKey: ['admin-tenants'] });
    },
    onError: (error) => setNotice({ kind: 'error', text: (error as Error).message }),
  });

  /**
   * Ask, then ask again if the account is not empty.
   *
   * The second question is not a repeat of the first: the server's refusal names
   * how many Pages, connections and jobs would be destroyed, and that sentence
   * is what the operator is being asked to accept.
   */
  const removeTenant = (id: string, name: string) => {
    const typed = window.prompt(t('admin.confirmDeleteTenant').replace('{name}', name));
    if (typed === null) return;

    destroy.mutate(
      { id, confirmName: typed },
      {
        onError: (error) => {
          if (error instanceof ApiError && error.reason === 'NOT_EMPTY') {
            if (window.confirm(error.message)) {
              destroy.mutate({ id, confirmName: typed, force: true });
            }
            return;
          }
          setNotice({ kind: 'error', text: (error as Error).message });
        },
      },
    );
  };

  const tenants = useQuery({
    queryKey: ['admin-tenants'],
    queryFn: api.adminTenants,
  });
  const plans = useQuery({
    queryKey: ['admin-plans'],
    queryFn: api.adminPlans,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin-tenants'] });
    void queryClient.invalidateQueries({ queryKey: ['admin-stats'] });
  };

  const onError = (error: unknown) =>
    setNotice({
      kind: 'error',
      text: error instanceof ApiError ? error.message : t('common.requestFailed'),
    });

  const create = useMutation({
    mutationFn: () => api.createTenant(form),
    onSuccess: () => {
      setForm({ name: '', slug: '', planCode: '' });
      setNotice({ kind: 'ok', text: t('admin.tenantCreated') });
      refresh();
    },
    onError,
  });

  const changePlan = useMutation({
    mutationFn: ({ id, planCode }: { id: string; planCode: string }) =>
      api.changeTenantPlan(id, planCode),
    onSuccess: () => {
      setNotice({ kind: 'ok', text: t('admin.planChanged') });
      refresh();
    },
    onError,
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status, reason }: { id: string; status: string; reason?: string }) =>
      api.setTenantStatus(id, status, reason),
    onSuccess: () => {
      setNotice({ kind: 'ok', text: t('admin.statusChanged') });
      refresh();
    },
    onError,
  });

  if (tenants.isLoading) return <div className="loading">{t('common.loading')}</div>;

  return (
    <>
      {notice && (
        <div className={notice.kind === 'ok' ? 'notice ok' : 'notice error'}>{notice.text}</div>
      )}

      <div className="grid-wrap">
        <table className="grid">
          <thead>
            <tr>
              <th>{t('admin.tenant')}</th>
              <th>{t('admin.plan')}</th>
              <th>{t('common.status')}</th>
              <th>{t('admin.stat.users')}</th>
              <th>{t('admin.stat.pages')}</th>
              <th>{t('common.date')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {tenants.data?.tenants.map((tenant) => (
              <tr key={tenant.id}>
                <td>
                  <strong>{tenant.name}</strong>
                  <br />
                  <span className="mono tiny muted">{tenant.slug}</span>
                  <br />
                  {/* The way into an account's users — and so the only way to
                      reset a password, or add the first one. */}
                  <button
                    type="button"
                    className="ghost tiny"
                    onClick={() =>
                      setOpenTenant((open) =>
                        open?.id === tenant.id ? null : { id: tenant.id, name: tenant.name },
                      )
                    }
                  >
                    {t('admin.manageUsers')}
                  </button>
                  <button
                    type="button"
                    className="ghost tiny"
                    onClick={() => {
                      const next = window.prompt(t('admin.renamePrompt'), tenant.name);
                      if (next && next.trim() && next.trim() !== tenant.name) {
                        rename.mutate({ id: tenant.id, name: next.trim() });
                      }
                    }}
                  >
                    {t('common.edit')}
                  </button>
                </td>
                <td>
                  <select
                    value={tenant.plan.code}
                    style={{ maxWidth: 170 }}
                    onChange={(event) =>
                      changePlan.mutate({
                        id: tenant.id,
                        planCode: event.target.value,
                      })
                    }
                  >
                    {plans.data?.plans.map((plan) => (
                      <option key={plan.code} value={plan.code}>
                        {plan.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <span
                    className={`badge ${
                      tenant.status === 'ACTIVE'
                        ? 'ok'
                        : tenant.status === 'SUSPENDED'
                          ? 'warn'
                          : 'err'
                    }`}
                  >
                    {tenant.status}
                  </span>
                  {tenant.suspendedReason && (
                    <div className="tiny muted">{tenant.suspendedReason}</div>
                  )}
                </td>
                <td>{tenant.counts.users}</td>
                <td>{tenant.counts.pages}</td>
                <td className="small">{fmt.date(tenant.createdAt)}</td>
                <td>
                  {tenant.status === 'ACTIVE' ? (
                    <button
                      type="button"
                      className="danger small"
                      onClick={() => {
                        const reason = window.prompt(t('admin.suspendPrompt'));
                        if (reason === null) return;
                        setStatus.mutate({
                          id: tenant.id,
                          status: 'SUSPENDED',
                          reason: reason || undefined,
                        });
                      }}
                    >
                      {t('admin.suspend')}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="small"
                      onClick={() => setStatus.mutate({ id: tenant.id, status: 'ACTIVE' })}
                    >
                      {t('admin.reactivate')}
                    </button>
                  )}
                  <button
                    type="button"
                    className="danger small"
                    disabled={destroy.isPending}
                    onClick={() => removeTenant(tenant.id, tenant.name)}
                  >
                    <Icon.trash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {openTenant && (
        <TenantUsers
          tenantId={openTenant.id}
          tenantName={openTenant.name}
          onClose={() => setOpenTenant(null)}
        />
      )}

      <div className="card composer">
        <h3>{t('admin.newTenant')}</h3>

        <label htmlFor="tenant-name">{t('admin.tenantName')}</label>
        <input
          id="tenant-name"
          value={form.name}
          onChange={(event) => setForm({ ...form, name: event.target.value })}
        />

        <label htmlFor="tenant-slug">{t('admin.tenantSlug')}</label>
        <input
          id="tenant-slug"
          className="ltr"
          placeholder="acme-media"
          value={form.slug}
          onChange={(event) => setForm({ ...form, slug: event.target.value })}
        />
        <p className="muted tiny">{t('admin.slugHint')}</p>

        <label htmlFor="tenant-plan">{t('admin.plan')}</label>
        <select
          id="tenant-plan"
          value={form.planCode}
          onChange={(event) => setForm({ ...form, planCode: event.target.value })}
        >
          <option value="">{t('admin.selectPlan')}</option>
          {plans.data?.plans.map((plan) => (
            <option key={plan.code} value={plan.code}>
              {plan.name}
            </option>
          ))}
        </select>

        <button
          type="button"
          className="primary"
          onClick={() => create.mutate()}
          disabled={!form.name || !form.slug || !form.planCode || create.isPending}
        >
          <Icon.plus />
          {t('admin.createTenant')}
        </button>
      </div>
    </>
  );
}

/*
 * Names for the catalogue keys the product ships with.
 *
 * The catalogue is editable from this very console, so a key may exist that
 * this dictionary has never heard of. The API's own `name` is the fallback,
 * which is what lets an administrator add a limit and price it immediately
 * rather than waiting for a release that knows its name.
 */
const QUOTA_LABELS: Record<string, TranslationKey> = {
  META_ACCOUNTS: 'admin.limit.maxMetaAccounts',
  PAGES: 'admin.limit.maxPages',
  USERS: 'admin.limit.maxUsers',
  META_APPS: 'account.usage.metaApps',
  AGENT_CALLS_PER_MONTH: 'admin.limit.maxAgentCallsPerMonth',
  AUTO_REPLIES_PER_MONTH: 'admin.limit.maxAutoRepliesPerMonth',
  POSTS_PER_DAY: 'account.usage.postsPerDay',
  COMMENTS_PER_DAY: 'account.usage.commentsPerDay',
};

const FEATURE_LABELS: Record<string, TranslationKey> = {
  PUBLISHING: 'admin.feature.featurePublishing',
  COMMENTS: 'admin.feature.featureComments',
  COMMENT_REPLIES: 'admin.feature.featureCommentReplies',
  AUTOMATION: 'admin.feature.featureAutomation',
  PUBLIC_SOURCE: 'admin.feature.featurePublicSource',
  API_ACCESS: 'admin.feature.featureApiAccess',
  OWN_META_APP: 'account.featureName.OWN_META_APP',
};

function catalogueLabel(
  t: (key: TranslationKey) => string,
  dictionary: Record<string, TranslationKey>,
  key: string,
  apiName: string,
): string {
  const translationKey = dictionary[key];
  return translationKey ? t(translationKey) : apiName;
}

function Plans() {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const [editing, setEditing] = useState<Record<string, Record<string, unknown>>>({});
  const [notice, setNotice] = useState<{
    kind: 'ok' | 'error';
    text: string;
  } | null>(null);

  const plans = useQuery({
    queryKey: ['admin-plans'],
    queryFn: api.adminPlans,
  });
  // The editor's fields *are* the catalogue, so it is fetched rather than
  // compiled in.
  const catalogue = useQuery({
    queryKey: ['admin-catalogue'],
    queryFn: api.adminCatalogue,
  });

  const save = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      api.updatePlan(id, patch),
    onSuccess: () => {
      setNotice({ kind: 'ok', text: t('admin.planSaved') });
      setEditing({});
      void queryClient.invalidateQueries({ queryKey: ['admin-plans'] });
    },
    // The API refuses a limit that a tenant already exceeds, and names them.
    onError: (error) =>
      setNotice({
        kind: 'error',
        text: error instanceof ApiError ? error.message : t('common.requestFailed'),
      }),
  });

  if (plans.isLoading || catalogue.isLoading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  const patchOf = (id: string) => editing[id] ?? {};

  const setField = (id: string, field: string, value: unknown) =>
    setEditing((current) => ({
      ...current,
      [id]: { ...(current[id] ?? {}), [field]: value },
    }));

  /**
   * Stage a change to one entitlement or one limit.
   *
   * Merged into the group already staged rather than replacing it, so ticking
   * two features sends both. The patch carries only what was touched — the API
   * leaves every key it was not sent alone, which is what stops a single edit
   * from resetting a plan.
   */
  const setGroupField = (
    id: string,
    group: 'features' | 'quotas',
    key: string,
    value: boolean | number | null,
  ) =>
    setEditing((current) => {
      const patch = current[id] ?? {};
      const existing = (patch[group] as Record<string, unknown> | undefined) ?? {};
      return {
        ...current,
        [id]: { ...patch, [group]: { ...existing, [key]: value } },
      };
    });

  return (
    <>
      <p className="muted small">{t('admin.plansHint')}</p>
      {notice && (
        <div className={notice.kind === 'ok' ? 'notice ok' : 'notice error'}>{notice.text}</div>
      )}

      {plans.data?.plans.map((plan) => {
        const patch = patchOf(plan.id);
        const dirty = Object.keys(patch).length > 0;
        const value = (field: string) =>
          (patch[field] ?? (plan as unknown as Record<string, unknown>)[field]) as never;

        const stagedFeatures = (patch.features as Record<string, boolean> | undefined) ?? {};
        const stagedQuotas = (patch.quotas as Record<string, number | null> | undefined) ?? {};
        const featureValue = (key: string) => stagedFeatures[key] ?? plan.features[key] ?? false;
        const quotaValue = (key: string) =>
          key in stagedQuotas ? stagedQuotas[key] : (plan.quotas[key] ?? null);

        return (
          <article key={plan.id} className="card">
            <header className="card-head">
              <h3>
                {plan.name} <span className="mono tiny muted">{plan.code}</span>
              </h3>
              <span className="badge">
                {plan.tenantCount} {t('admin.tenantsOnPlan')}
              </span>
            </header>

            <div className="plan-fields">
              {catalogue.data?.quotas.map((quota) => {
                const current = quotaValue(quota.key);
                return (
                  <div key={quota.key}>
                    <label htmlFor={`${plan.id}-${quota.key}`}>
                      {catalogueLabel(t, QUOTA_LABELS, quota.key, quota.name)}
                      {quota.period !== 'TOTAL' && (
                        <span className="tiny muted">
                          {' '}
                          {t(`account.period.${quota.period}` as TranslationKey)}
                        </span>
                      )}
                    </label>
                    <input
                      id={`${plan.id}-${quota.key}`}
                      type="number"
                      min={0}
                      className="ltr"
                      // An empty field is unlimited, not zero. Zero forbids the
                      // thing outright, and the two must stay tellable apart.
                      placeholder={t('admin.unlimited')}
                      value={current ?? ''}
                      onChange={(event) =>
                        setGroupField(
                          plan.id,
                          'quotas',
                          quota.key,
                          event.target.value === '' ? null : Number(event.target.value),
                        )
                      }
                    />
                  </div>
                );
              })}
              <div>
                <label htmlFor={`${plan.id}-price`}>{t('admin.price')}</label>
                <input
                  id={`${plan.id}-price`}
                  type="number"
                  min={0}
                  className="ltr"
                  value={value('priceMonthly') as number}
                  onChange={(event) =>
                    setField(plan.id, 'priceMonthly', Number(event.target.value))
                  }
                />
              </div>
            </div>

            <label style={{ marginTop: 16 }}>{t('admin.entitlements')}</label>
            <div className="feature-toggles">
              {catalogue.data?.features.map((feature) => (
                <label key={feature.key} className="checkbox-row" style={{ margin: 0 }}>
                  <input
                    type="checkbox"
                    checked={featureValue(feature.key)}
                    onChange={(event) =>
                      setGroupField(plan.id, 'features', feature.key, event.target.checked)
                    }
                  />
                  {catalogueLabel(t, FEATURE_LABELS, feature.key, feature.name)}
                  {/* Priceable before it ships, so a plan can be designed
                      around the roadmap; refused at runtime until then. */}
                  {feature.status === 'COMING_SOON' && (
                    <span className="badge warn tiny">{t('account.blockedBy.UNRELEASED')}</span>
                  )}
                </label>
              ))}
            </div>

            {dirty && (
              <div className="actions">
                <button
                  type="button"
                  className="primary small"
                  onClick={() => save.mutate({ id: plan.id, patch })}
                  disabled={save.isPending}
                >
                  <Icon.check />
                  {t('common.save')}
                </button>
                <button
                  type="button"
                  className="ghost small"
                  onClick={() =>
                    setEditing((current) => {
                      const next = { ...current };
                      delete next[plan.id];
                      return next;
                    })
                  }
                >
                  {t('common.cancel')}
                </button>
              </div>
            )}
          </article>
        );
      })}
    </>
  );
}

/**
 * Which Meta permissions a customer may be asked for.
 *
 * This was two environment variables until 23 September 2026, and that is why
 * it is a screen now: a value only reachable over SSH is a value nobody watches
 * go stale. It went stale, the login dialog went on asking every customer for
 * three permissions Meta had not approved, and Meta answered by refusing the
 * dialog outright — nobody outside the team could sign in for an hour, and no
 * screen in this product could have said why.
 *
 * Nothing here is inferred. Meta publishes no endpoint that answers "is this
 * approved for my app", so the status moves when a person acts on Meta's
 * verdict, and this is where they act.
 */
function MetaPermissions() {
  const { t } = useI18n();
  const fmt = useFormatters();
  const queryClient = useQueryClient();

  const permissions = useQuery({
    queryKey: ['meta-permissions'],
    queryFn: api.metaPermissions,
  });

  const save = useMutation({
    mutationFn: ({ scope, status }: { scope: string; status: MetaPermissionStatus }) =>
      api.setMetaPermissionStatus(scope, status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['meta-permissions'] });
      // The Connections screen asks a different question with the same answer.
      void queryClient.invalidateQueries({ queryKey: ['connections'] });
    },
  });

  if (permissions.isLoading) return <div className="loading">{t('common.loading')}</div>;
  if (permissions.isError) return <div className="notice error">{t('admin.superAdminOnly')}</div>;

  const rows = permissions.data?.permissions ?? [];

  return (
    <div className="stack">
      <p className="muted small">{t('admin.permissions.intro')}</p>

      <div className="grid-wrap">
        <table className="grid">
          <thead>
            <tr>
              <th>{t('admin.permissions.scope')}</th>
              <th>{t('admin.permissions.channel')}</th>
              <th>{t('admin.permissions.status')}</th>
              <th>{t('admin.permissions.lastCustomer')}</th>
              <th>{t('admin.permissions.note')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td className="mono tiny ltr">{row.scope}</td>
                <td className="tiny muted">{row.channel ?? '—'}</td>
                <td>
                  <select
                    value={row.status}
                    disabled={save.isPending}
                    onChange={(event) =>
                      save.mutate({
                        scope: row.scope,
                        status: event.target.value as MetaPermissionStatus,
                      })
                    }
                  >
                    <option value="IN_DEVELOPMENT">{t('admin.permissions.IN_DEVELOPMENT')}</option>
                    <option value="PENDING_APPROVAL">
                      {t('admin.permissions.PENDING_APPROVAL')}
                    </option>
                    <option value="PUBLISHED">{t('admin.permissions.PUBLISHED')}</option>
                  </select>
                </td>
                {/*
                  Evidence, not a status. Meta withholds an unapproved permission
                  from accounts with no role on the app, so a grant to an
                  ordinary tenant is proof it really is published — and a
                  published one that stops appearing here is the first sign that
                  something moved at Meta's end.
                */}
                <td className="tiny muted">
                  {row.lastGrantedToCustomerAt
                    ? fmt.dateTime(row.lastGrantedToCustomerAt)
                    : t('admin.permissions.neverCustomer')}
                </td>
                <td className="text-cell small">{row.note ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {save.isError && <div className="notice error">{t('common.requestFailed')}</div>}
    </div>
  );
}
